import { useQuery } from '@tanstack/react-query';
import { TextTooltip } from '@ui/components/TextTooltip';
import { Interval } from '@ui/constants/poll';
import { useDataChainId } from '@ui/hooks/datachain';
import { Text } from '@ui/lib/park/ui/text';
import { slvmonQuery } from '@ui/services/backend/hooks/slvmon/slvmonQuery';
import SLVMON_ABI from '@ui/services/contract/abis/SLVMON';
import { getTokenAddress } from '@ui/services/contract/constants/address';
import { useTranslation } from 'next-i18next';
import { Flex, HStack, styled, VStack } from 'styled-system/jsx';
import { formatUnits } from 'viem';
import { useReadContract } from 'wagmi';

/**
 * V2 APY block for the LVMON action card:
 * sectionLabel "APY · 1D AVG" + 32px mono green value on the left,
 * "Total staked N LVMON" mono meta bottom-aligned on the right.
 */
export const StakeStatsV3 = () => {
  const { t } = useTranslation();
  const chainId = useDataChainId();
  const slvmonAddress = getTokenAddress('sLVMON', chainId);

  const { data: totalAssets } = useReadContract({
    address: slvmonAddress,
    abi: SLVMON_ABI,
    functionName: 'totalAssets',
    query: {
      enabled: !!slvmonAddress,
      refetchInterval: Interval.SLOW,
    },
  });

  const { data: apy } = useQuery({
    queryKey: slvmonQuery.getAverageApyKey({ days: 1 }),
    queryFn: slvmonQuery.getAverageApyFn({ days: 1 }),
    refetchInterval: Interval.SLOW,
  });

  const APY_DISPLAY_CAP = 1500;
  const apyPercentValue = typeof apy === 'number' ? apy * 100 : null;
  const formattedApy = apyPercentValue === null
    ? '-'
    : apyPercentValue > APY_DISPLAY_CAP
      ? `> ${APY_DISPLAY_CAP}`
      : apyPercentValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
  const apyTooltipContent = apyPercentValue === null
    ? 'Annual Percentage Yield'
    : `Annual Percentage Yield (Actual: ${apyPercentValue.toLocaleString(undefined, { maximumFractionDigits: 2 })}%)`;

  const formattedTotalAssets = totalAssets
    ? Number(formatUnits(totalAssets, 18)).toLocaleString(undefined, {
      maximumFractionDigits: 0,
    })
    : '-';

  return (
    <Flex
      w="full"
      alignItems="flex-end"
      justifyContent="space-between"
      gap="16px"
      flexWrap="wrap"
    >
      <VStack alignItems="flex-start" gap="5px">
        <HStack gap={1} alignItems="center">
          <Text
            textStyle="v2.sectionLabel"
            fontSize="10.5px"
            letterSpacing="0.07em"
            color="v2.text.subtle"
          >
            {t('lvmon.stake.apyLabel', 'APY · 1d avg')}
          </Text>
          <TextTooltip
            content={apyTooltipContent}
            triggerCss={{
              textDecoration: 'none',
              cursor: 'help',
            }}
          >
            <Text
              as="span"
              display="inline-flex"
              alignItems="center"
              justifyContent="center"
              w="13px"
              h="13px"
              borderRadius="9999px"
              border="1px solid"
              borderColor="v2.border.strong"
              color="v2.text.subtle"
              fontSize={9}
              lineHeight="9px"
            >
              ?
            </Text>
          </TextTooltip>
        </HStack>
        <Text
          textStyle="numbers.xxlarge"
          color="v2.long.default"
        >
          {formattedApy}%
        </Text>
      </VStack>

      <HStack gap="5px" alignItems="center" pb="6px">
        <styled.img src="/tokens/LVMON.svg" width="14px" height="14px" />
        <Text textStyle="numbers.xsmall" fontWeight="400" color="v2.text.subtle">
          {t('lvmon.stake.totalStakedLabel', 'Total staked')}{' '}
          <styled.span color="v2.text.secondary" fontWeight="500">
            {formattedTotalAssets}
          </styled.span>{' '}
          LVMON
        </Text>
      </HStack>
    </Flex>
  );
};
